import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Field, Input, Select, Textarea } from '../../ui/Field.js';

/**
 * Compatibilité : champs historiques de l'espace garage, rendus par `src/ui/Field`.
 * Libellé au-dessus, police 16 px (pas de zoom automatique sur iOS), zone tactile de 48 px.
 * Seuls les champs vraiment obligatoires portent « obligatoire ». Code neuf : `Field` + `Input`.
 */
export function TextField({
  label,
  hint,
  required,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode }) {
  return (
    <Field label={label} hint={hint} required={required} className={className}>
      <Input {...rest} />
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  required,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: ReactNode; hint?: ReactNode }) {
  return (
    <Field label={label} hint={hint} required={required} className={className}>
      <Textarea {...rest} />
    </Field>
  );
}

export function SelectField({
  label,
  hint,
  required,
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <Field label={label} hint={hint} required={required} className={className}>
      <Select {...rest}>{children}</Select>
    </Field>
  );
}

/** Case à cocher avec une grande zone cliquable (toute la ligne). */
export function CheckboxField({
  label,
  checked,
  onChange,
  disabled,
  description,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  description?: ReactNode;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className="flex min-h-(--ctl-md) cursor-pointer items-start gap-3 rounded-xl border border-line bg-surface p-3 transition-colors hover:border-line-strong has-[:checked]:border-brand-line has-[:checked]:bg-brand-soft"
    >
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 size-5 shrink-0"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="block text-base text-ink">{label}</span>
        {description ? <span className="block text-sm text-ink-3">{description}</span> : null}
      </span>
    </label>
  );
}

export type CivilityValue = 'mme' | 'm' | null;

export const CIVILITY_LABELS: Record<'mme' | 'm', string> = { mme: 'Madame', m: 'Monsieur' };

/**
 * Civilité du client (« Bonjour Madame Durand » dans les e-mails). Facultative : « Non précisée »
 * donne « Bonjour » suivi du nom. Trois grands boutons, utilisables d'une main.
 */
export function CivilityField({
  value,
  onChange,
  disabled,
}: {
  value: CivilityValue;
  onChange: (v: CivilityValue) => void;
  disabled?: boolean;
}) {
  const options: { v: CivilityValue; label: string }[] = [
    { v: 'mme', label: CIVILITY_LABELS.mme },
    { v: 'm', label: CIVILITY_LABELS.m },
    { v: null, label: 'Non précisée' },
  ];
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-ink-2">Civilité</span>
      <div
        role="group"
        aria-label="Civilité"
        className="grid grid-cols-3 gap-1 rounded-lg bg-sunken p-0.5 ring-1 ring-line ring-inset"
      >
        {options.map((o) => (
          <button
            key={o.label}
            type="button"
            aria-pressed={value === o.v}
            disabled={disabled}
            onClick={() => onChange(o.v)}
            className={`min-h-(--ctl-sm) rounded-lg px-2 text-sm font-medium transition-[background-color,color,box-shadow] duration-150 ${
              value === o.v ? 'bg-surface text-ink shadow-sm ring-1 ring-line' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Interrupteur (rôle `switch`) : pour les interrupteurs de compte. */
export function Switch({
  label,
  checked,
  onChange,
  disabled,
  description,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  description?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex min-h-(--ctl-md) w-full items-center justify-between gap-3 py-2 text-left disabled:opacity-50"
    >
      <span>
        <span className="block text-base text-ink">{label}</span>
        {description ? <span className="block text-sm text-ink-3">{description}</span> : null}
      </span>
      <span
        aria-hidden
        className={`relative h-6 w-10 shrink-0 rounded-full ring-1 ring-black/5 ring-inset transition-colors duration-200 ${checked ? 'bg-brand-strong' : 'bg-line-strong'}`}
      >
        <span
          className={`absolute top-0.5 size-5 rounded-full bg-surface shadow-sm transition-transform duration-200 ease-out ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
        />
      </span>
    </button>
  );
}
